import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    Type,
    ViewChild
} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {ICsPagingParams} from '@app/shared/cs-components/cs-components.interface';
import {
    ICsAgGridJs,
    ICsDetailGridHeightObject,
    ICsGridAction,
    ICsGridApi
} from '@app/shared/cs-ag-grid-js/cs-ag-grid-js.interface';
import {
    CsAgGridJsAngularComponentResolverService
} from '@app/shared/cs-ag-grid-js/cs-ag-grid-js-angular-component-resolver.service';
import {environment} from '../../../environments/environment';
import cloneDeep from 'lodash.clonedeep';
import {GridApi} from "ag-grid-community";

declare var agGrid;

@Component({
    selector: 'app-cs-ag-grid-js',
    templateUrl: './cs-ag-grid-js.component.html',
    styleUrls: ['cs-ag-grid-js.component.scss']
})
export class CsAgGridJsComponent implements OnInit, AfterViewInit, OnDestroy {

    private static instanceSeq = 0;
    private readonly instanceId = ++CsAgGridJsComponent.instanceSeq;

    @ViewChild('grid') grid: ElementRef;

    @Input() config: ICsAgGridJs;
    @Input() params: any; // Should be passed only for child grid or detail grid

    @Output() onGridInit = new EventEmitter<ICsGridApi>();
    @Output() onGridDataLoad = new EventEmitter();
    @Output() onAllRowSelection = new EventEmitter();
    @Output() onRowSelection = new EventEmitter<{ data: object; isSelected: boolean }>();
    // @Output() sendParentNode = new EventEmitter()
    filterWidthChangeEvent = new Subject<any>();
    appliedFilter: any[] = [];
    searchText = '';
    /** Extra keys (e.g. userId, tagIds) when the search bar's "@"/"#" dropdowns are enabled and it emits
     * an object instead of a plain string. */
    searchExtraParams: any = {};
    totalRecords: number;
    paginationParams: ICsPagingParams = {};
    gridOptions: any;
    gridApi: any;
    /** Shown until `createGrid()` succeeds; avoids relying on `gridApi` alone for *ngIf + forces CD after hide. */
    showGridSkeleton = true;
    gridLayout: 'normal' | 'autoHeight' = 'autoHeight';
    gridListAddOnData: any;
    gridList$: Subscription;
    isDataLoading = true;
    gridComputedHeight: number;
    private gridApiMap: Map<number | string, GridApi> = new Map();
    private currentlySelectedRows: object[] = [];
    private isSelectionLoading: boolean = false;
    isDetailGrid = false;
    private resizeObserver: any;
    private resizeDebounceTimer: ReturnType<typeof setTimeout>;

    constructor(
        private _componentResolver: CsAgGridJsAngularComponentResolverService,
        private renderer: Renderer2,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {
    }

    /** Dev-only lifecycle tracing: filter console by `[CsAgGridJs` */
    private dbg(message: string, detail?: object) {
        if (detail !== undefined) {
            console.log('[CsAgGridJs #' + this.instanceId + '] ' + message, detail);
        } else {
            console.log('[CsAgGridJs #' + this.instanceId + '] ' + message);
        }
    }

    ngOnInit() {
        this.dbg('init: component setup (config clone, options, license)');
        this.cloneConfig();
        this.setConfig();
        this.setGridOptions();
        this.setGridLicense();
        this.isDetailGrid = this.params && Array.isArray(this.params.currentParent) && this.params.currentParent.length > 0;
    }

    /**
     * AG Grid is attached to #grid in the template. @ViewChild is only available after the view initializes;
     * creating the grid in ngOnInit silently skipped and never emitted onGridInit, breaking all parents.
     *
     * Defer createGrid/setCSRMData to the next macrotask so the skeleton flag is not cleared in the same CD turn
     * as the first template check. Otherwise dev-mode checkNoChanges throws ExpressionChangedAfterItHasBeenCheckedError.
     */
    ngAfterViewInit() {
        this.dbg('afterViewInit: scheduling grid create + initial data (next tick)');
        setTimeout(() => {
            this.createGrid();
            this.setCSRMData();
        }, 0);
    }

    private cloneConfig() {
        this.config = cloneDeep(this.config);
    }

    private setConfig() {
        if (this.config.gridConfig.rowReOrder) {
            this.config.gridConfig.colDefs.unshift({
                field: 'row_drag',
                headerName: '',
                rowDrag: true,
                minWidth: 48,
                maxWidth: 48,
            });
        }

        const dataKeys = this.config.gridConfig.dataResKeys;
        this.config.gridConfig.dataResKeys = {
            countKey: dataKeys ? (dataKeys['countKey'] || 'count') : 'count',
            dataKey: dataKeys ? (dataKeys['dataKey'] || 'data') : 'data'
        };

        this.appliedFilter = this.config.hideFilterPanel ? [] : (this.config.filterPanelConfig['appliedFilter'] || []);

        this.filterWidthChangeEvent.next(this.appliedFilter);

        this.paginationParams['pageSize'] = this.isPagination ? (this.config.gridConfig.rowsPerPage || 5) : null;
    }

    private setGridOptions() {
        this.gridOptions = {
            components: {
                ...this._componentResolver.csAgCustomComponents(),
                ...this.resolvedAngularComponents()
            },

            // Note: It will be enabled (or) handled based on future spec, if required.
            // selectionColumnDef: {
            //     pinned: true
            // },

            defaultColDef: {
                flex: 1,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                sortable: false,
                unSortIcon: true,
                wrapText: this.config.gridConfig.wrapText !== false,
                autoHeight: this.config.gridConfig.rowHeight == null && this.config.gridConfig.wrapText !== false,
                wrapHeaderText: this.config.gridConfig.wrapHeaderText !== false,
                autoHeaderHeight: this.config.gridConfig.wrapHeaderText !== false,
                enableCellChangeFlash: true,
                resizable: this.config.gridConfig.isColumnResizable === true,
                tooltipComponent: this.config.gridConfig.customtooltipComponent,
            },

            columnDefs: this.config.gridConfig.colDefs,
            ...(this.config.gridConfig.rowHeight != null && { rowHeight: this.config.gridConfig.rowHeight }),
            tooltipShowDelay: this.config.gridConfig.tooltipShowDelay,
            tooltipShowMode: this.config.gridConfig.tooltipShowMode,
            autoGroupColumnDef: {
                headerName: 'Group',
                resizable: true,
                minWidth: 268,
                pinned: 'left',
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: (params) => {
                        if (params.value){
                            const count = params.data.groupCount || 0; //data.groupCount
                            let displayValue = params.valueFormatted || params.value;

                            if (!params.valueFormatted && params.node && params.node.rowGroupColumn && params.node.rowGroupColumn.getColDef) {
                                const rowGroupColumn = params.node.rowGroupColumn;
                                const rowGroupColDef = rowGroupColumn.getColDef();

                                if (rowGroupColDef && typeof rowGroupColDef.valueFormatter === 'function') {
                                    try {
                                        displayValue = rowGroupColDef.valueFormatter({
                                            ...params,
                                            value: params.value,
                                            data: params.data,
                                            node: params.node,
                                            colDef: rowGroupColDef,
                                            column: rowGroupColumn,
                                        });
                                    } catch (e) {
                                        displayValue = params.value;
                                    }
                                }
                            }

                            return `${displayValue} (${count})`;
                        }
                    }
                }
            },
            domLayout: 'autoHeight',
            suppressMovableColumns: true,
            rowClass: this.focusEnablingClass,
            suppressCellFocus: this.config.gridConfig.rowFocus !== true,
            suppressServerSideFullWidthLoadingRow: true,
            masterDetail: !!this.config.gridConfig.enableDetailGrid,
            suppressContextMenu: true,
            scrollbarWidth: 4,
            noRowsOverlayComponent: 'noRowsOverlay',
            suppressMoveWhenRowDragging: true,
            suppressPaginationPanel: true,
            suppressScrollOnNewData: true, // Prevents unnecessary multiple API calls
            blockLoadDebounceMillis: 500, // Ensures loading indicator stays visible for UX
            rowGroupPanelShow: this.config.gridConfig.enableRowGroupPanel ? 'always' : 'never',
            treeData: this.config.gridConfig.treeData,
            // keepDetailRows: true,
            // keepDetailRowsCount: 1,
            // Note: Below, will be enabled (or) handled based on the future spec, if required.
            // suppressCsvExport: true,
            // suppressExcelExport: true,
            // rowDragMultiRow: true,
            // rowDragEntireRow: true,
            // detailRowAutoHeight: true,

            onGridSizeChanged: (event: any) => this.updateDomLayout(),
            onCellClicked: (params: any) => this.onOpenChildGrid(this.config.gridConfig.isCollapseOtherParents, params),
            onModelUpdated: (event: any) => this.updateDomLayout(),
            onRowDragMove: (event: any) => this.swapRows(event),
            onDragStopped: (event: any) => this.onRowDropped(event),
            onRowSelected: (event: any) => this.rowSelection(event),
            onSelectionChanged: (event: any) => this.allRowSelection(event),
            onSortChanged: (event: any) => this.resetSelectedRows(),
            onCellFocused: this.config.gridConfig.onCellKeyDown,
            onColumnRowGroupChanged: () => this.resetSelectedRows(),
        };

        this.setServerSideProps();
        this.setPaginationProps();
        this.setRowSelectionConfig();
        this.setDetailGridConfig();
    }

    onOpenChildGrid(collapse: boolean, params: any) {
        // only act if expand/collapse icon clicked
        if (collapse && params.colDef.cellRenderer === 'detailCellRenderer' && params.node.master) {
            setTimeout(() => {
                if (params.node.expanded) {
                    this.gridApi.forEachNode(node => {
                        if (node !== params.node && node.expanded) {
                            node.setExpanded(false);   // collapse others
                            node['expandedColId'] = null;
                        }
                    });
                }
            });
        }
    }

    private setServerSideProps() {
        if (this.isCSRMEnabled) {
            return;
        }

        this.gridOptions['rowModelType'] = 'serverSide';

        if(this.gridOptions.treeData && this.config.gridConfig.uniqueRowKeyField){
            this.gridOptions['treeData'] = this.gridOptions.treeData;
            this.gridOptions['isServerSideGroup'] = dataItem => {
                return true;
            };
            this.gridOptions['getServerSideGroupKey'] = dataItem => {
                return dataItem[this.config.gridConfig.uniqueRowKeyField];
            };
        }


        this.gridOptions['serverSideStoreType'] = 'partial';
        //this.gridOptions['serverSideStoreType'] = 'full';
        this.gridOptions['serverSideInitialRowCount'] = this.config.gridConfig.loaderRowCount || 5;
        this.gridOptions['serverSideDatasource'] = this.dataSource;
        this.gridOptions['maxConcurrentDatasourceRequests'] = 1;
    }

    private setPaginationProps() {
        if (this.isPagination) {
            this.gridOptions['pagination'] = true;
            this.gridOptions['paginationPageSize'] = this.paginationParams['pageSize'];
            this.gridOptions['cacheBlockSize'] = this.paginationParams['pageSize'];
        }
    }

    get focusEnablingClass(): string {
        let enablingClass = '';
        const isSpace = () => enablingClass ? ' ' : '';

        enablingClass += this.config.gridConfig.rowFocus ? (isSpace() + 'cs-enable-row-focus') : '';
        enablingClass += this.config.gridConfig.cellFocus ? (isSpace() + 'cs-enable-cell-focus') : '';

        return enablingClass;
    }

    get isPagination() {
        return !this.isCSRMEnabled && !this.config.hidePagination;
    }

    private get isCSRMEnabled() {
        return this.config.gridConfig.enableClientSideRowModel;
    }

    private resolvedAngularComponents() {
        if (this.config['customComponents']) {
            const resolvedComponents = {};

            Object.entries(this.config.customComponents).forEach(([name, component]) => {
                resolvedComponents[name] = this._componentResolver.resolveAngularRendererComponent(component as Type<any>);
            });

            return resolvedComponents;
        }

        return null;
    }

    private setRowSelectionConfig() {
        if (this.config.gridConfig.rowSelection) {
            this.gridOptions['rowSelection'] = {
                mode: 'multiRow',
                groupSelects: "descendants",
                headerCheckbox: this.config.gridConfig.hideHeaderRowSelection ? false : true,
                isRowSelectable : this.config.gridConfig.hasOwnProperty("isRowSelectable") ? this.config.gridConfig.isRowSelectable:() => true,
            };
        }
        if(this.config.gridConfig.pinRowSelection) {
            this.gridOptions['selectionColumnDef'] = {
                pinned: this.config.gridConfig.pinRowSelection,
            };
        }

        // Added listener to handle group selection
        if (this.config.gridConfig.uniqueRowKeyField) {
            const keyField = this.config.gridConfig.uniqueRowKeyField;
            const rowMatchesGroupPath = (row: any, keys: string[], fields: string[]): boolean => {
                if (!row || !Array.isArray(keys) || !Array.isArray(fields) || keys.length !== fields.length) {
                    return false;
                }
                for (let i = 0; i < keys.length; i++) {
                    if (row[fields[i]] !== keys[i]) {
                        return false;
                    }
                }
                return true;
            };
            this.gridOptions['onRowSelected'] = (event) => {
                const isGroupHead = 'expanded' in event.node && !event.node.expanded;
                const isSelected = event.node.__selected;
                const buildGroupPath = (node) => {
                    const keys: string[] = [];
                    const fields: string[] = [];
                    while (node) {
                        if (node.key !== undefined && node.field !== undefined) {
                            keys.unshift(node.key);
                            fields.unshift(node.field);
                        }
                        node = node.parent;
                    }
                    return { keys, fields };
                };
                const updateSelection = (rows: any[], add: boolean) => {
                    const rowMap = new Map<string | number, any>(
                        this.currentlySelectedRows.map<[string | number, any]>(row => [row[keyField], row])
                    )
                    rows.forEach(row => {
                        const key = row[keyField];
                        if (key !== undefined && key !== null && key !== '') {
                            if (add) rowMap.set(key, row);
                            else rowMap.delete(key);
                        }
                    });
                    this.currentlySelectedRows = Array.from(rowMap.values());
                };
                if (isGroupHead && isSelected) {
                    const { keys, fields } = buildGroupPath(event.node);
                    this.loadGroupChildren(keys, fields).then(children => {
                        updateSelection([event.data].concat(children || []), true);
                        this.cdr.markForCheck();
                    });
                } else if (!isSelected) {
                    if (isGroupHead) {
                        const { keys, fields } = buildGroupPath(event.node);
                        // Remove group row and any cached rows matching this group path (no API call)
                        const matchingRows = this.currentlySelectedRows.filter(
                            (row: any) => row !== event.data && rowMatchesGroupPath(row, keys, fields)
                        );
                        const rowsToRemove = [event.data].concat(matchingRows);
                        updateSelection(rowsToRemove, false);
                        this.cdr.markForCheck();
                    } else {
                        updateSelection([event.data], false);
                    }
                }
                this.rowSelection(event);
                this.cdr.markForCheck();
            };
        }
    }

    private loadGroupChildren(groupKeys: any,rowGroupCols: any): Promise<any[]> {
        // Example: call same API your SSRM datasource uses
        const reqParams = {
            page: this.isPagination ? (this.paginationParams.currentPage || 1) : null,
            limit: this.paginationParams.pageSize,
            groupKeys: groupKeys ? JSON.stringify(groupKeys):null,
            rowGroupCols:groupKeys ? JSON.stringify(rowGroupCols):null,
        };
        this.isSelectionLoading = true;

        return new Promise((resolve, reject) => {
            this.config.gridConfig.gridList({ reqParams }).subscribe(
                res => {
                    const data = res[this.config.gridConfig.dataResKeys.dataKey] || [];
                    resolve(data);
                    this.isSelectionLoading = false; // Done loading
                },
                err => {
                    reject(err)
                    this.isSelectionLoading = false; // Fail, still stop loading
                }
            );
        });
    }

    private setDetailGridConfig() {
        if (this.config.gridConfig.enableDetailGrid && this.config.detailGridConfig) {
            const detailConfig = this.config.detailGridConfig;

            const paramsCb = detailConfig.params;
            const rendererCb = detailConfig.renderer;
            // const heightCb = detailConfig.gridHeight;

            this.gridOptions['detailCellRenderer'] = (rendererParams: any): HTMLElement => {
                const detailMetaData = rendererParams.node.parent['detailMetaData'];
                // const detailHeight: ICsDetailGridHeightObject | number = heightCb ? heightCb(detailMetaData) : 310;
                const detailRenderer = rendererCb ? rendererCb(detailMetaData) : 'detailGridRenderer';
                const RendererClass = this.gridOptions.components[detailRenderer];
                const instance = new RendererClass();
                const rowId = (rendererParams.node.id || rendererParams.node.rowIndex).replace("detail_", "");
                this.gridApiMap.set(rowId, this.gridApi);
                rendererParams['currentParent'] = this.params && this.params['parentTree']
                    ? (this.params['parentTree'] as any[]).concat([{ api: this.gridApi, rowId: rowId, config: this.config }])
                    : [{ api: this.gridApi, rowId: rowId, config: this.config }];

                instance.init(rendererParams);
                // const rowNodes = this.getDisplayedRowNodes(rendererParams.api) || [];
                //
                // const computedHeight = typeof detailHeight == 'number' ? detailHeight : Math.min(
                //     (detailHeight.topBarHeight || 0) + detailHeight.headerHeight + (detailHeight.padding || 0) + rowNodes.length * detailHeight.rowHeight,
                //     detailHeight.maxHeight
                // );
                // setTimeout(() => {
                //     rendererParams.node.setRowHeight(computedHeight);
                //     rendererParams.api.onRowHeightChanged();
                // });

                return instance.getGui();
            };

            this.gridOptions['detailCellRendererParams'] = (params: any) => {
                return {config: paramsCb(params.node.parent['detailMetaData'])};
            };
        }
    }

    private setGridLicense() {
        const agGridGlobal = (window as any).agGrid;
        if (agGridGlobal && agGridGlobal.LicenseManager && typeof agGridGlobal.LicenseManager.setLicenseKey === 'function') {
            agGridGlobal.LicenseManager.setLicenseKey(environment.agGridEnterpriseLicenseKey);
        }
    }

    private createGrid() {
        const agGridGlobal = (window as any).agGrid;
        if (!agGridGlobal || typeof agGridGlobal.createGrid !== 'function') {
            console.error(
                '[CsAgGridJs #' + this.instanceId + '] create: FAILED — window.agGrid missing. Include ag-grid-enterprise.min.js in index.html before the app boots.'
            );
            return;
        }

        if (!this.grid || !this.grid.nativeElement) {
            this.dbg('create: skipped — #grid element not ready');
            return;
        }

        try {
            this.dbg('create: calling agGrid.createGrid()', {
                rowModel: this.isCSRMEnabled ? 'clientSide' : 'serverSide',
                isDetailGrid: this.isDetailGrid
            });
            this.gridApi = agGridGlobal.createGrid(this.grid.nativeElement, this.gridOptions);

            if (!this.gridApi) {
                console.warn('[CsAgGridJs #' + this.instanceId + '] create: createGrid returned no api');
                return;
            }
            this.dbg('create: grid instance ready; emitting onGridInit');
            this.onGridInit.emit(this.gridInstance);
            this.updateDragListenersToHeaders();
            this.observeGridContainerResize();
            this.showGridSkeleton = false;
            this.cdr.detectChanges();
        } catch (error) {
            console.error('[CsAgGridJs #' + this.instanceId + '] create: error', error);
        }
    }

    private observeGridContainerResize() {
        const gridEl = this.grid.nativeElement as HTMLElement;
        const containerToObserve = gridEl.closest('.grid-wrapper') || gridEl.parentElement || gridEl;
        const ResizeObserverCtor = (window as any).ResizeObserver;
        if (!containerToObserve || !ResizeObserverCtor) {
            return;
        }
        this.resizeObserver = new ResizeObserverCtor(() => {
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = setTimeout(() => {
                requestAnimationFrame(() => {
                    if (!this.gridApi) {
                        return;
                    }
                    this.updateDomLayout();
                    if (typeof this.gridApi.sizeColumnsToFit === 'function') {
                        //this.gridApi.sizeColumnsToFit();
                    }
                    this.cdr.markForCheck();
                });
            }, 100);
        });
        this.resizeObserver.observe(containerToObserve);
    }

    private get dataSource() {
        return ({getRows: (params: any) => this.setGridData(params)});
    }

    private setCSRMData() {
        if (this.isCSRMEnabled) {
            this.setGridData();
        }
    }

    protected get gridInstance(): ICsGridApi {
        return {
            refreshGridData: this.refreshGridData,
            addOnData: this.getAddOnData,
            getSelectedRows: this.getSelectedRows,
            resetPagination: this.resetPagination,
            refreshHeaders: this.updateDragListenersToHeaders.bind(this),
            gridApi: this.gridApi,
            getPaginationParams: () => ({
                page: this.paginationParams.currentPage != null ? this.paginationParams.currentPage : 1,
                limit: this.paginationParams.pageSize != null ? this.paginationParams.pageSize : 10,
            }),
            // mainGridApi: !this.params ? this.gridApi : null
        };
    }

    isDisable(action: ICsGridAction): boolean {
        return typeof action.disable === 'function' ? action.disable() : action.disable;
    }

    isHide(action: ICsGridAction): boolean {
        return typeof action.hide === 'function' ? action.hide() : action.hide;
    }

    swapRows = (event: any) => {
        if (!event.node
            || !event.overNode
            || !event.vDirection
            || (event.node.id === event.overNode.id)) {
            return;
        }

        requestAnimationFrame(() => {
            const draggedRowElement = this.grid.nativeElement.querySelector(`[row-id="${event.node.id}"]`);
            const overRowElement = this.grid.nativeElement.querySelector(`[row-id="${event.overNode.id}"]`);
            const draggingRowIndex = draggedRowElement.getAttribute('row-index');
            const overRowIndex = overRowElement.getAttribute('row-index');

            if ((event.vDirection === 'down' && (draggingRowIndex < overRowIndex))
                || (event.vDirection === 'up' && (draggingRowIndex > overRowIndex))) {

                const rowHeight = event.node.rowHeight;
                const draggingRowPos = +draggingRowIndex * rowHeight;
                const overRowPos = +overRowIndex * rowHeight;

                this.renderer.setAttribute(draggedRowElement, 'row-index', overRowIndex);
                this.renderer.setAttribute(overRowElement, 'row-index', draggingRowIndex);
                this.renderer.setStyle(draggedRowElement, 'transform', `translateY(${overRowPos}px)`);
                this.renderer.setStyle(overRowElement, 'transform', `translateY(${(draggingRowPos)}px)`);

                this.updateSwappingRowClasses(event, draggedRowElement, +overRowIndex);
                this.updateSwappingRowClasses(event, overRowElement, +draggingRowIndex);

                event.node.data['swappedOrder'] = +overRowIndex;
                event.overNode.data['swappedOrder'] = +draggingRowIndex;
            }
        });
    }

    updateSwappingRowClasses(event: any, rowElement: Element, rowIndex: number) {
        let totalRows = event.api.getDisplayedRowCount();
        totalRows = totalRows > this.paginationParams.pageSize ? this.paginationParams.pageSize : totalRows;

        this.renderer[rowIndex === 0 ? 'addClass' : 'removeClass'](rowElement, 'ag-row-first');
        this.renderer[rowIndex === (totalRows - 1) ? 'addClass' : 'removeClass'](rowElement, 'ag-row-last');

        this.renderer.addClass(rowElement, (rowIndex % 2) === 0 ? 'ag-row-even' : 'ag-row-odd');
        this.renderer.removeClass(rowElement, (rowIndex % 2) === 0 ? 'ag-row-odd' : 'ag-row-even');
    }

    onRowDropped(event: any) {
        if (this.config.gridConfig.rowReOrder) {
            const swappedRows = this.getDisplayedRowNodes(event.api)
                .filter((rowNode: any) => 'swappedOrder' in rowNode.data)
                .map((rowNode: any) => ({id: rowNode.data['id'], order: rowNode.data['swappedOrder']}))
                .sort((current, next) => current['order'] > next['order'] ? 1 : (current['order'] < next['order'] ? -1 : 0));

            if (swappedRows.length) {
                this.config.gridConfig
                    .updateRowOrder(swappedRows)
                    .subscribe(res => this.refreshGridData());
            }
        }
    }

    getDisplayedRowNodes = (api: any) => {
        if (!api || typeof api.getRenderedNodes !== 'function') {
            return [];
        }
        try {
            return api.getRenderedNodes() || [];
        } catch (error) {
            console.warn('Error getting displayed row nodes:', error);
            return [];
        }
    }

    rowSelection(event: any) {
        if (event.source === 'uiSelectAll') {
            return;
        }

        const eventParam = {
            data: event.data,
            isSelected: event.node.isSelected()
        };

        const selectionCb = this.config.gridConfig.onRowSelection;

        this.ngZone.run(() => {
            selectionCb ? selectionCb(this.gridInstance, eventParam) : this.onRowSelection.emit(eventParam);
        });
    }

    allRowSelection(event: any) {
        const runUpdate = () => {
            const allSelectionCb = this.config.gridConfig.onAllRowSelection;
            this.ngZone.run(() => {
                allSelectionCb ? allSelectionCb(this.gridInstance) : this.onAllRowSelection.emit();
                this.cdr.markForCheck();
            });
        };

        // Header checkbox (select all / uncheck all) can come through with different sources
        // depending on AG Grid version/config. Handle all of them consistently.
        if (['apiSelectAll', 'checkboxSelected', 'uiSelectAll'].includes(event.source)) {
            // Header checkbox (select all / uncheck all): sync cache and still update UI
            setTimeout(() => {
                if (this.gridApi) {
                    const selectedNodes = this.gridApi.getSelectedNodes() || [];
                    if (selectedNodes.length === 0) {
                        this.currentlySelectedRows = [];
                    } else {
                        // Provide one "row selection" style callback for header select-all,
                        // so consumers relying on rowSelection context (e.g. customerId) still work.
                        const firstNode: any = selectedNodes[0];
                        if (firstNode && firstNode.data) {
                            const selectionCb = this.config.gridConfig.onRowSelection;
                            const eventParam = { data: firstNode.data, isSelected: true };
                            this.ngZone.run(() => {
                                selectionCb ? selectionCb(this.gridInstance, eventParam) : this.onRowSelection.emit(eventParam);
                            });
                        }
                    }
                }
                runUpdate();
            }, 0);
            return;
        }

        runUpdate();
    }

    getSelectedRows = (): object[] => {
        if (!this.gridApi) {
            return this.currentlySelectedRows.length ? this.currentlySelectedRows.slice() : [];
        }
        // Use getSelectedNodes() so count matches grid selection (getRenderedNodes only returns visible rows)
        const selectedNodes = this.gridApi.getSelectedNodes ? this.gridApi.getSelectedNodes() : [];
        const selectedDataFromGrid = (selectedNodes || [])
            .filter((node: any) => node && node.data)
            .map((node: any) => node.data);
        const keyField = 'uniqueRowKeyField' in this.config.gridConfig ? this.config.gridConfig.uniqueRowKeyField : undefined;
        if (keyField) {
            const rowMap = new Map<string | number, any>();
            [this.currentlySelectedRows, selectedDataFromGrid].forEach(rows => {
                (rows || []).forEach((row: any) => {
                    const key = row[keyField];
                    if (key !== undefined && key !== null && key !== '') {
                        rowMap.set(key, row);
                    }
                });
            });
            const deduplicated = Array.from(rowMap.values());
            this.currentlySelectedRows = deduplicated;
            return deduplicated;
        }
        return selectedDataFromGrid;
    };

    get isHeaderActionsAvailable(): boolean {
        return !!(this.config.headerPanelConfig.actions.length
            && this.config.headerPanelConfig.actions.find(action => !this.isHide(action)));
    }

    getAddOnData = () => {
        return this.gridListAddOnData || null;
    }

    onActionClick(action: ICsGridAction) {
        if (this.isDisable(action)) {
            return;
        }

        action.onClick({action: action.id});
    }

    onSearch(val: string | { searchText: string; [key: string]: any }) {
        if (typeof val === 'string') {
            this.searchText = val;
            this.searchExtraParams = {};
        } else {
            const { searchText, ...extraParams } = val;
            this.searchText = searchText;
            this.searchExtraParams = extraParams;
        }
        this.resetPagination();
        this.refreshGridData();
    }

    onFilter(param: object | number, action: 'apply' | 'remove' | 'update') {
        if (action === 'apply') {
            this.appliedFilter.push(param);
        } else if (action === 'remove') {
            this.appliedFilter.splice(param as number, 1);
        } else if (action === 'update') {
            const { index, ...filterData } = param as { index: number; [key: string]: any };
            this.appliedFilter[index] = filterData;
        }

        this.resetPagination();
        this.refreshGridData();
    }

    updatePagingParams(params: ICsPagingParams) {
        this.paginationParams = params;

        if (params.currentEvent && params.currentEvent !== 'none') {
            this.gridApi.setGridOption('paginationPageSize', this.paginationParams.pageSize);
            this.gridApi.setGridOption('cacheBlockSize', this.paginationParams.pageSize);
            this.goToPage();
            this.refreshGridData();
        }
    }

    goToPage(page?: number) {
        if (this.isPagination) {
            if (page) {
                this.paginationParams.currentPage = page;
            }
            this.gridApi.paginationGoToPage(this.paginationParams.currentPage);
        }
    }

    updateDomLayout() {
        const gridEl = this.grid.nativeElement as HTMLElement;
        const gridViewportEl = gridEl.querySelector('.ag-body-viewport .ag-viewport.ag-center-cols-viewport') as HTMLElement;

        if (!gridViewportEl) {
            return;
        }

        const containerHeight = gridEl.clientHeight;
        const headerHeight = this.gridApi.getSizesForCurrentTheme().headerHeight;
        const headerBorderWidth = 1;
        const gridHeight = headerHeight + headerBorderWidth + gridViewportEl.offsetHeight;
        const domLayout = gridHeight > containerHeight ? 'normal' : 'autoHeight';

        if (domLayout !== this.gridLayout) {
            this.gridApi.setGridOption('domLayout', domLayout);
            this.gridLayout = domLayout;
            // Run change detection so [ngClass]="{'h-100': gridLayout === 'normal'}" updates
            // (onGridSizeChanged/onModelUpdated run outside Angular zone and don't trigger CD)
            this.cdr.markForCheck();
        }
        if(this.params) {
            const defaultRowHeight = 310;
            const rowNodes = this.getDisplayedRowNodes(this.gridApi);
            const detailHeight: ICsDetailGridHeightObject | number = this.params.config.gridHeight && rowNodes.length ? this.params.config.gridHeight : defaultRowHeight;
            const computedHeight = typeof detailHeight == 'number' ? detailHeight : Math.min(
                (detailHeight.topBarHeight || 0) + detailHeight.headerHeight + (detailHeight.padding || 0) + rowNodes.length * detailHeight.rowHeight,
                detailHeight.maxHeight
            );
            // const detailGridFound = rowNodes.find((node) => node.detail === true);
            // this.sendParentNode.emit(detailGridFound);

            if(this.params.parentTree.length == 2 && this.gridComputedHeight !== computedHeight-42) {
                const parentBeforeRowNode = this.params.parentTree[1];
                const parentBeforeRowNodes = this.getDisplayedRowNodes(parentBeforeRowNode.api);
                const count = parentBeforeRowNodes.filter(node => {
                    const id = node.id;
                    return typeof id === 'string' && !id.includes('detail_');
                }).length;
                const parentDetailHeight: ICsDetailGridHeightObject | number = parentBeforeRowNode.config.gridHeight && rowNodes.length ? parentBeforeRowNode.config.gridHeight : defaultRowHeight;
                const parentComputedHeight = typeof parentDetailHeight == 'number' ? parentDetailHeight : (parentDetailHeight.topBarHeight || 0) + parentDetailHeight.headerHeight + (parentDetailHeight.padding || 0) + count * parentDetailHeight.rowHeight
                const parentHeight = typeof parentDetailHeight == 'number' ? parentDetailHeight : Math.min(((computedHeight*(parentBeforeRowNodes.length-count))+parentComputedHeight), parentDetailHeight.maxHeight);
                const grandParentRowNode = this.params.parentTree[0];
                grandParentRowNode.api.getRowNode('detail_'+grandParentRowNode.rowId).setRowHeight(parentHeight);
                grandParentRowNode.api.onRowHeightChanged();
                this.params.node.setRowHeight(computedHeight);
                this.params.api.onRowHeightChanged();
            }
            else if(this.params.parentTree.length == 1 && this.gridComputedHeight !== computedHeight) {
                this.params.node.setRowHeight(computedHeight);
                this.params.api.onRowHeightChanged();
            }
            this.gridComputedHeight = computedHeight;
        }
    }

    showOrHideNoRowsOverlay = (show: boolean) => this.gridApi[show ? 'showNoRowsOverlay' : 'hideOverlay']();

    refreshGridData = () => {
        this.showOrHideNoRowsOverlay(false);
        if (this.isCSRMEnabled) {
            this.setCSRMData();
        } else {
            this.gridApi.refreshServerSide({route: [], purge: true});
            this.gridApi.setGridOption('serverSideDatasource', this.dataSource);
        }
        this.resetSelectedRows();
    }

    private setGridData(params?: any) {
        const requestParams = {
            searchText: this.searchText,
            filters: this.appliedFilter ? JSON.stringify(this.appliedFilter) : null,
            // Explicit default (not just omitted) so a stale tagIds carried over in appliedData
            // (e.g. from a previous tab's response params) is always overridden, the same way
            // searchText already is, instead of leaking into requests where no tag is selected.
            ...(this.config.filterPanelConfig && this.config.filterPanelConfig.showTagListDropDown ? { tagIds: '' } : {}),
            ...this.searchExtraParams
        };

        if (!this.isCSRMEnabled && params) {
            requestParams['page'] = this.isPagination ? (this.paginationParams.currentPage || 1) : null;
            requestParams['limit'] = this.paginationParams.pageSize;
            requestParams['sort'] = params.request.sortModel[0] ? JSON.stringify(params.request.sortModel[0]) : null;
            requestParams['groupKeys'] = params.request.groupKeys ? JSON.stringify(params.request.groupKeys) : null;
            requestParams['rowGroupCols'] = params.request.rowGroupCols
                ? JSON.stringify(params.request.rowGroupCols.map((item: object) => item['field']))
                : null;
        }

        this.dbg('data: request', {
            rowModel: this.isCSRMEnabled ? 'clientSide' : 'serverSide',
            requestParams: requestParams,
            hasServerSideParams: !!params
        });

        const parentNode = params && params.parentNode;
        const parentNodeInfo = parentNode != null
            ? { expanded: !!parentNode.expanded, key: parentNode.key }
            : null;

        this.gridList$ = this.config.gridConfig.gridList({
            reqParams: requestParams,
            parentNode: parentNodeInfo
        })
            .subscribe(res => {
                const data = res[this.config.gridConfig.dataResKeys.dataKey];
                this.gridListAddOnData = {...res['addOnData'], displayRecordCount: data.length};

                if (this.isCSRMEnabled) {
                    this.totalRecords = +res[this.config.gridConfig.dataResKeys.countKey];
                    this.showOrHideNoRowsOverlay(!this.totalRecords);
                    this.gridApi.setGridOption('rowData', data);
                } else {
                    const isGroupedCall = requestParams['groupKeys'] && JSON.parse(requestParams['groupKeys']).length > 0;
                    let recordCount;
                    if (isGroupedCall) {
                        recordCount = data.length;
                    } else {
                        this.totalRecords = +res[this.config.gridConfig.dataResKeys.countKey];
                        const isPartialRecordsInPage = data.length < requestParams['limit'];
                        recordCount = isPartialRecordsInPage ? data.length : this.totalRecords;
                    }

                    this.showOrHideNoRowsOverlay(!recordCount);
                    params.success({rowData: data, rowCount: recordCount});

                    // Default-select child rows when tree/group is expanded and data loaded from API
                    // if (isGroupedCall && this.config.gridConfig.rowSelection && this.config.gridConfig.uniqueRowKeyField && Array.isArray(data) && data.length > 0) {
                    //     const keyField = this.config.gridConfig.uniqueRowKeyField;
                    //     const keySet = new Set(
                    //         data
                    //             .map((row: any) => row[keyField])
                    //             .filter((k: any) => k !== undefined && k !== null && k !== '')
                    //     );
                    //     setTimeout(() => {
                    //         if (!this.gridApi) return;
                    //         this.gridApi.forEachNode((node: any) => {
                    //             if (node.data && keySet.has(node.data[keyField])) {
                    //                 node.setSelected(true);
                    //             }
                    //         });
                    //         const rowMap = new Map<string | number, any>(
                    //             this.currentlySelectedRows.map((row: any): [string | number, any] => [row[keyField], row])
                    //         );
                    //         data.forEach((row: any) => {
                    //             const key = row[keyField];
                    //             if (key !== undefined && key !== null && key !== '') {
                    //                 rowMap.set(key, row);
                    //             }
                    //         });
                    //         this.currentlySelectedRows = Array.from(rowMap.values());
                    //         this.cdr.markForCheck();
                    //     }, 0);
                    // }
                }

                this.isDataLoading = false;

                this.dbg('data: loaded', {
                    rowCount: data && data.length != null ? data.length : 0,
                    totalRecords: this.totalRecords,
                    rowModel: this.isCSRMEnabled ? 'clientSide' : 'serverSide'
                });

                this.config.gridConfig.onGridDataLoad ?
                    this.config.gridConfig.onGridDataLoad(this.gridInstance)
                    : this.onGridDataLoad.emit(this.gridInstance);
            }, err => {
                console.error('[CsAgGridJs #' + this.instanceId + '] data: load failed', err);
            });
    }

    /*******************Custom grouping drag and drop**************************/
    updateDragListenersToHeaders() {
        if(this.config.gridConfig.enableCustomGroupPanel) {
            setTimeout(() => {
                const headers = document.querySelectorAll('.ag-header-cell');
                headers.forEach((headerEl: HTMLElement) => {
                    const colId = headerEl.getAttribute('col-id');
                    if (!colId) return;

                    const col = this.gridApi.getColumn(colId);
                    const colDef = col.getColDef();

                    // Skip if column is not groupable
                    if (!colDef.enableCustomRowGroup) return;

                    const labelEl = headerEl.querySelector('.ag-header-cell-label');
                    if (!labelEl) return;

                    // Prevent duplicate icon
                    if (labelEl.querySelector('.cs-drag-icon')) return;

                    let dragPreviewEl: HTMLElement;

                    // Create drag icon container
                    const iconContainer = document.createElement('span');
                    iconContainer.classList.add('cs-drag-icon');
                    iconContainer.innerHTML = `<i class="fa-solid fa-grip-dots-vertical" style="cursor: move"></i>`;

                    // ✅ Make only the icon draggable
                    iconContainer.setAttribute('draggable', 'true');

                    // Apply styling
                    iconContainer.style.display = 'flex';
                    iconContainer.style.flex = '1';
                    iconContainer.style.justifyContent = 'end';

                    // Insert before the sort icon
                    labelEl.insertBefore(iconContainer, labelEl.lastElementChild);

                    // Add dragstart and dragend handlers to the icon only
                    iconContainer.addEventListener('dragstart', (e: DragEvent) => {
                        e.stopPropagation(); // Prevent bubbling to header
                        e.dataTransfer.setData('application/vnd.aggrid.coldef+json', JSON.stringify(colDef));
                        e.dataTransfer.effectAllowed = 'move';

                        // Create custom drag image
                        dragPreviewEl = document.createElement('div');
                        dragPreviewEl.innerText = colDef.headerName || colId;
                        dragPreviewEl.style.padding = '4px 10px';
                        dragPreviewEl.style.background = '#e1fcda';
                        dragPreviewEl.style.border = '1px solid #c3e6cb';
                        dragPreviewEl.style.borderRadius = '4px';
                        dragPreviewEl.style.fontSize = '13px';
                        dragPreviewEl.style.fontFamily = 'inherit';
                        dragPreviewEl.style.color = '#2e7d32';
                        dragPreviewEl.style.position = 'absolute';
                        dragPreviewEl.style.top = '-1000px';
                        dragPreviewEl.id = "gridDraggedElement";

                        document.body.appendChild(dragPreviewEl);
                        e.dataTransfer.setDragImage(dragPreviewEl, 0, 0);
                    });

                    iconContainer.addEventListener('dragend', () => {
                        if (dragPreviewEl) {
                            document.body.removeChild(dragPreviewEl);
                            dragPreviewEl = null;
                        }
                    });
                });
            });

        }
    }

    resetPagination = () => {
        if (this.isPagination) {
            this.goToPage(1);
            this.totalRecords = null;
            this.isDataLoading = true;
        }
    }

    resetSelectedRows() {
        if (this.config.gridConfig.rowSelection) {
            this.gridApi.deselectAll();
            this.currentlySelectedRows = [];
        }
    }

    ngOnDestroy() {
        this.dbg('destroy: teardown start — release timers, observers, subscriptions, grid api');

        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.gridList$) {
            this.gridList$.unsubscribe();
            this.gridList$ = null;
        }
        if (this.gridApiMap && this.gridApiMap.size) {
            this.gridApiMap.clear();
        }
        if (this.gridApi && typeof this.gridApi.destroy === 'function') {
            try {
                this.dbg('destroy: calling gridApi.destroy()');
                this.gridApi.destroy();
            } catch (e) {
                console.warn('[CsAgGridJs #' + this.instanceId + '] destroy: gridApi.destroy error', e);
            }
        }
        this.gridApi = null;
        this.showGridSkeleton = true;
        this.currentlySelectedRows = [];
        this.dbg('destroy: teardown complete — references cleared');
    }
}
